/**
 * C3 employees view-model — pure display shaping for the employees list + dual
 * search screen (national-id grouping, avatar initials/colour cycling, status →
 * tag mapping, department name resolution, and pagination range/guards). Kept out
 * of the .vue so node:test can cover the logic directly (the companies repo has no
 * SFC test runner). Plain CommonJS so it is both node:test-requireable and
 * webpack-importable.
 */

// §2 employee status → .tag modifier class (mirrors the C3 mockup).
var STATUS_TAG = { active: 'ok', suspended: 'review', left: 'muted' };

function statusTag(status) {
  return STATUS_TAG[status] || 'muted';
}

/** Group a 14-digit national id into LTR-readable 4-digit chunks (display only). */
function groupNationalId(nid) {
  var s = String(nid == null ? '' : nid).replace(/\s+/g, '');
  if (!s) return '—';
  return s.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** First two glyphs of the name — the avatar initials (e.g. "أحمد سمير" → "أح"). */
function initials(name) {
  var s = String(name == null ? '' : name).trim();
  if (!s) return '؟';
  return Array.from(s).slice(0, 2).join('');
}

/** Cycle the 3 avatar palette variants ('', a2, a3) by row index, per the mockup. */
function avatarClass(i) {
  var n = Number(i) || 0;
  return ['', 'a2', 'a3'][((n % 3) + 3) % 3];
}

/** Build an { id: nameAr } lookup so a row's deptId resolves to a readable name. */
function buildDeptMap(departments) {
  var map = {};
  var rows = departments || [];
  for (var i = 0; i < rows.length; i++) map[String(rows[i].id)] = rows[i].nameAr;
  return map;
}

function deptName(deptId, map) {
  var m = map || {};
  return m[String(deptId)] || '—';
}

/** Bilingual entity name — nameEn||nameAr under en, nameAr||nameEn under ar. */
function entityName(entity, locale) {
  if (!entity) return '—';
  if (locale === 'en') return entity.nameEn || entity.nameAr || '—';
  return entity.nameAr || entity.nameEn || '—';
}

/** §1 list-meta → "عرض start–end من total" range (start/end are 1-based). */
function pageInfo(meta, count) {
  var m = meta || {};
  var page = Math.max(1, Number(m.page) || 1);
  var limit = Math.max(1, Number(m.limit) || 20);
  var total = Number(m.total) || 0;
  var c = Number(count) || 0;
  if (total === 0 || c === 0) return { start: 0, end: 0, total: total };
  var start = (page - 1) * limit + 1;
  return { start: start, end: start + c - 1, total: total };
}

function hasPrev(meta) {
  var m = meta || {};
  return (Number(m.page) || 1) > 1;
}

function hasNext(meta) {
  var m = meta || {};
  var page = Number(m.page) || 1;
  var limit = Number(m.limit) || 20;
  var total = Number(m.total) || 0;
  return page * limit < total;
}

// ---------------------------------------------------------------------------
// Bulk policy assignment (POST /employees/assign-policy) ----------------------
// ---------------------------------------------------------------------------
var SKIP_REASONS = ['EMPLOYEE_NOT_FOUND', 'COMPANY_MISMATCH', 'EMPLOYEE_NOT_ACTIVE', 'ALREADY_ASSIGNED', 'HAS_OTHER_POLICY'];
var MAX_ASSIGN_IDS = 5000;

/** Only active policies are assignable (the backend answers 422 POLICY_NOT_ACTIVE otherwise). */
function assignablePolicies(policies) {
  return (policies || []).filter(function (p) {
    return p && (!p.status || p.status === 'active');
  });
}

/**
 * Request body: exactly one of employeeIds / filters. mode 'selected' sends the
 * ticked ids; mode 'unassigned' sends filters { unassignedOnly: true } (HR is
 * pinned to its own company server-side). overwrite is only sent when true.
 */
function buildAssignRequest(opts) {
  var o = opts || {};
  var body = { policyId: String(o.policyId) };
  if (o.mode === 'unassigned') body.filters = { unassignedOnly: true };
  else body.employeeIds = (o.employeeIds || []).map(String);
  if (o.overwrite) body.overwrite = true;
  return body;
}

/** '' when the request can be sent, else an i18n suffix under employees.assign.errors.*. */
function validateAssign(opts) {
  var o = opts || {};
  if (!o.policyId) return 'policyRequired';
  if (o.mode !== 'unassigned') {
    var n = (o.employeeIds || []).length;
    if (n === 0) return 'noSelection';
    if (n > MAX_ASSIGN_IDS) return 'tooMany';
  }
  return '';
}

/**
 * Result -> display model: counts, the skipped rows with the employee name
 * resolved from { id: name }, and per-reason totals (unknown reasons kept as-is).
 */
function summarizeAssign(result, nameMap) {
  var r = result || {};
  var skipped = (r.skipped || []).map(function (s) {
    var id = s && s.employeeId != null ? String(s.employeeId) : '';
    return { employeeId: id, name: (nameMap && nameMap[id]) || (id ? '#' + id : '—'), reason: (s && s.reason) || '' };
  });
  var totals = {};
  skipped.forEach(function (s) {
    totals[s.reason] = (totals[s.reason] || 0) + 1;
  });
  return {
    matched: Number(r.matched) || 0,
    assigned: Number(r.assigned) || 0,
    skipped: skipped,
    reasonTotals: Object.keys(totals).map(function (k) {
      return { reason: k, count: totals[k], known: SKIP_REASONS.indexOf(k) !== -1 };
    }),
  };
}

/** Axios error -> i18n suffix under employees.assign.apiErrors.*. */
function assignErrorKey(err) {
  var res = err && err.response;
  var code = res && res.data && res.data.code;
  if (code === 'POLICY_NOT_FOUND' || code === 'POLICY_NOT_ACTIVE' || code === 'INVALID_ASSIGNMENT_TARGET') return code;
  if (res && res.status === 403) return 'FORBIDDEN';
  if (!res) return 'network';
  return 'generic';
}

/** Toggle one id in a selection (new array). */
function toggleId(selected, id) {
  var key = String(id);
  var list = selected || [];
  return list.indexOf(key) === -1 ? list.concat([key]) : list.filter(function (x) { return x !== key; });
}

/** Select-all for the visible rows: all selected -> clear those rows, else add them. */
function toggleAll(selected, rowIds) {
  var ids = (rowIds || []).map(String);
  var list = selected || [];
  var all = ids.length > 0 && ids.every(function (id) { return list.indexOf(id) !== -1; });
  if (all) return list.filter(function (x) { return ids.indexOf(x) === -1; });
  return list.concat(ids.filter(function (id) { return list.indexOf(id) === -1; }));
}

// ── B3 «الفلاتر عاوزها في dropdown» + the bug behind it ──────────────────────
//
// The screen used to fetch one page with only {page, limit, q} and then filter
// that page in the browser, because sending `status`/`deptId` was rejected as an
// unknown query property (the API's ValidationPipe is forbidNonWhitelisted).
// That silently lied to HR: filtering page 1 of 20 displayed "3 of 57", and
// paging past the first page dropped matches entirely. Both params are
// server-side now (deptId was added to EmployeesQueryDto), so the row count and
// the pager tell the truth.
//
// Param names are load-bearing: the API accepts `page`, `limit` (max 100), `q`,
// `status`, `deptId` — NOT `pageSize`, `take` or `search`, each of which is a
// 400. Keeping the mapping in one tested function is what stops that drifting.

/** The query for GET /employees. Empty filters are omitted, never sent blank. */
function buildListParams(state) {
  var s = state || {};
  var params = {
    page: s.page && s.page > 0 ? s.page : 1,
    limit: s.limit && s.limit > 0 ? Math.min(s.limit, 100) : 20,
  };
  var q = typeof s.q === 'string' ? s.q.trim() : '';
  if (q) params.q = q;
  // '' = "all"; only a real value narrows the list
  if (s.status) params.status = String(s.status);
  if (s.deptId !== undefined && s.deptId !== null && String(s.deptId) !== '') {
    params.deptId = String(s.deptId);
  }
  return params;
}

/** How many filters are narrowing the list — the badge on the dropdown toggle,
 *  so a collapsed panel can never hide WHY the list looks short. */
function activeFilterCount(state) {
  var s = state || {};
  var n = 0;
  if (s.status) n += 1;
  if (s.deptId !== undefined && s.deptId !== null && String(s.deptId) !== '') n += 1;
  if (typeof s.q === 'string' && s.q.trim()) n += 1;
  return n;
}

// ── B1 «الموظفين مفيش حذف فعاوز زرار حذف ويكن soft delete» ──────────────────

/** Server error → i18n key, so the row never shows a raw backend string. */
var DELETE_API_ERRORS = {
  EMPLOYEE_NOT_FOUND: 'notFound',
  COMPANY_CONTEXT_MISSING: 'forbidden',
};

function deleteErrorKey(err) {
  var e = err || {};
  var res = e.response;
  if (!res) return 'network';
  var code = res.data && res.data.code;
  if (code && DELETE_API_ERRORS[code]) return DELETE_API_ERRORS[code];
  if (res.status === 403) return 'forbidden';
  if (res.status === 404) return 'notFound';
  if (res.status === 409) return 'conflict';
  return 'generic';
}

// ── B2 «المساعد الذكي لسه مش شغال» ──────────────────────────────────────────
//
// Verified against production: POST /chatbot-qa/ask answers EVERY question with
// {answer:"", confidence:0, source:"fallback"} and HTTP 201, and the company Q&A
// bank is empty (total 0). The panel turned all of that — plus any 4xx/5xx —
// into one generic sentence, so there was no way to tell "no answer" from
// "broken" from "nobody has filled the bank yet".
//
// It also advertised something the endpoint cannot do. The placeholder promises
// «أحضر لي موظفي المبيعات الموقوفين» — a natural-language EMPLOYEE SEARCH —
// while /chatbot-qa/ask is a company FAQ bot (exact match, then refund/coverage
// intents, then a grounded LLM answer). It was never going to answer that.
//
// So employee questions are answered HERE, deterministically: parse the status
// word, the department name and whatever is left as a name, then run the normal
// server-side employee query. No LLM, no new endpoint, nothing to go 429 or
// time out — and it keeps working with the LLM disabled, which is how the
// deployment is configured. Anything that is NOT an employee question still
// goes to the FAQ bot.

/**
 * Status STEMS → the API's `status` value.
 *
 * Matching is by TOKEN, with a stem matched as a PREFIX of the token (Arabic
 * inflects at the end: موقوف / موقوفين / الموقوفين / موقوفة), never as a bare
 * substring. Substring matching was catastrophic for real names: «علي» contains
 * the stop-word «لي», «منى» contains «من», «كمال» contains «كم», «ابراهيم»
 * contains «هي» — so searching for any of those people fell through to the FAQ
 * bot, and «الحسابات» contains «ساب» so a whole department was read as
 * "employees who left".
 *
 * Stems of 1–2 characters must match the token EXACTLY, because a 2-letter
 * prefix matches a huge share of Arabic words (منى/من, كمال/كم, لي/ليلى).
 */
var STATUS_WORDS = [
  { value: 'suspended', stems: ['موقوف', 'متوقف', 'suspend', 'onhold'] },
  // «ساب» and «خرج» are deliberately NOT here: «السابق», «مخرج», «خرجت» would
  // all be read as departures.
  { value: 'left', stems: ['مغادر', 'استقال', 'left', 'resign', 'depart'] },
  { value: 'active', stems: ['نشط', 'نشيط', 'شغال', 'فعال', 'active'] },
];

/** Stems that say "this question is about employees". */
var EMPLOYEE_STEMS = ['موظف', 'عامل', 'مؤمن', 'employee', 'staff', 'worker'];

/** Stems that ask for a COUNT rather than a list. */
var COUNT_STEMS = ['كم', 'عدد', 'كام', 'count', 'many'];

/** Search verbs. On the employees screen these signal an employee lookup. */
var SEARCH_STEMS = [
  'ابحث', 'بحث', 'دور', 'لاقي', 'هات', 'احضر', 'اعرض', 'عرض', 'ورين', 'وريني',
  'find', 'search', 'show', 'list', 'get', 'lookup',
];

/**
 * Coverage / benefits vocabulary. A question carrying any of these belongs to
 * the FAQ bot, not the employee list — «ما هي مزايا الموظف في البوليصة؟»
 * mentions "الموظف" and was being hijacked into a name search for «مزايا».
 * An explicit status or department still wins, so «الموقوفين في البوليصة X»
 * stays an employee query.
 */
var FAQ_STEMS = [
  'مزايا', 'ميزه', 'تغطي', 'مغطا', 'مغطى', 'بوليصه', 'تعويض',
  'شبكه', 'مستشفي', 'عياده', 'خصم', 'اشتراك', 'فلوس',
  // Arabic verbs take ي/ت/ن prefixes, so a prefix match on «استرداد» misses
  // «يسترد». List the forms rather than guessing at affix stripping.
  'استرداد', 'استرد', 'يسترد', 'تسترد', 'ارجاع', 'يرجع',
  'coverage', 'covered', 'benefit', 'policy', 'refund', 'reimburse', 'network',
];

/** Filler that must never survive into the name search. */
var STOP_STEMS = [
  'لي', 'عايز', 'عاوز', 'محتاج', 'عن', 'في', 'من', 'قسم', 'اسم',
  'كل', 'جميع', 'ايه', 'مين', 'هم', 'اللي', 'الذين', 'هي', 'هو', 'ما', 'هل',
  'me', 'all', 'the', 'which', 'who', 'are', 'is', 'in', 'of', 'from',
  'department', 'please', 'my', 'our', 'name', 'named', 'called',
];

/**
 * Filler that collides with a real name once normalised. «على» (the
 * preposition) and «علي» (one of the most common Egyptian male names) both
 * become «علي» after folding ى→ي, so dropping it unconditionally made Ali
 * unsearchable. It is only filler when something follows it — the name people
 * are looking for is what the sentence ends on.
 */
var POSITIONAL_STOPS = ['علي'];

function normalizeArabic(text) {
  return String(text == null ? '' : text)
    .toLowerCase()
    // drop tashkeel + tatweel
    .replace(/[\u064B-\u0652\u0670\u0640]/g, '')
    // unify alef / ya / ta-marbuta so «موظفى» and «موظفة» match «موظفي»
    .replace(/[\u0622\u0623\u0625\u0627]/g, 'ا')
    .replace(/\u0649/g, 'ي')
    .replace(/\u0629/g, 'ه')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip the Arabic definite article so «الموقوفين» matches the stem «موقوف». */
function stripAl(token) {
  return token.indexOf('ال') === 0 && token.length > 3 ? token.slice(2) : token;
}

/**
 * Does one token carry this stem?
 * Long stem (3+): the token, with or without «ال», STARTS with it.
 * Short stem (1–2): the token must BE it. A 2-letter prefix is not evidence.
 */
function tokenMatchesStem(token, stem) {
  var st = normalizeArabic(stem);
  if (!st || !token) return false;
  var bare = stripAl(token);
  if (st.length <= 2) return token === st || bare === st;
  return token.indexOf(st) === 0 || bare.indexOf(st) === 0;
}

function anyStem(tokens, stems) {
  for (var i = 0; i < tokens.length; i++) {
    for (var j = 0; j < stems.length; j++) {
      if (tokenMatchesStem(tokens[i], stems[j])) return true;
    }
  }
  return false;
}

/**
 * Find a department by WHOLE WORDS, not substring.
 *
 * A raw indexOf matched mid-word, so a department literally named «مال» hit
 * «الاعمال» and a department «طب» hit «قطب» — giving the user a wrong filter
 * AND a mangled name search from the leftover characters. The department's
 * tokens must now appear consecutively in the question.
 *
 * @returns {{row:Object, at:number, len:number}|null} longest match wins
 */
function findDepartment(tokens, departments) {
  var rows = departments || [];
  var best = null;
  for (var d = 0; d < rows.length; d++) {
    var parts = normalizeArabic(rows[d].nameAr).split(' ').filter(Boolean);
    if (!parts.length) continue;
    for (var i = 0; i + parts.length <= tokens.length; i++) {
      var ok = true;
      for (var k = 0; k < parts.length; k++) {
        var tok = tokens[i + k];
        if (tok !== parts[k] && stripAl(tok) !== stripAl(parts[k])) {
          ok = false;
          break;
        }
      }
      if (ok && (!best || parts.length > best.len)) {
        best = { row: rows[d], at: i, len: parts.length };
      }
    }
  }
  return best;
}

/**
 * Turn a natural-language question into employee-list filters, or report that
 * it is not an employee question at all.
 *
 * @param {string} question
 * @param {Array<{id:string,nameAr:string}>} departments
 * @returns {{isEmployeeQuery:boolean, wantsCount:boolean, status:string,
 *            deptId:string, deptName:string, q:string}}
 */
function parseEmployeeQuery(question, departments) {
  var out = {
    isEmployeeQuery: false,
    wantsCount: false,
    status: '',
    deptId: '',
    deptName: '',
    q: '',
  };
  var norm = normalizeArabic(question);
  if (!norm) return out;

  var tokens = norm.split(' ').filter(Boolean);
  // Normalising is for MATCHING only. The name search is sent to the server
  // verbatim, because the backend does a plain `contains` on fullName — a
  // normalised «مني» would not match a stored «منى».
  var rawTokens = String(question == null ? '' : question)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  var raw = function (i) {
    return rawTokens.length === tokens.length ? rawTokens[i] : tokens[i];
  };

  var dept = findDepartment(tokens, departments);
  var idx = [];
  for (var t = 0; t < tokens.length; t++) idx.push(t);
  if (dept) {
    out.deptId = String(dept.row.id);
    out.deptName = dept.row.nameAr;
    idx.splice(dept.at, dept.len);
  }

  out.wantsCount = anyStem(tokens, COUNT_STEMS);
  var mentionsEmployees = anyStem(tokens, EMPLOYEE_STEMS);
  var mentionsSearch = anyStem(tokens, SEARCH_STEMS);
  var mentionsFaq = anyStem(tokens, FAQ_STEMS);

  var kept = [];
  for (var ri = 0; ri < idx.length; ri++) {
    var pos = idx[ri];
    var tok = tokens[pos];
    if (!tok) continue;

    // positional filler: only filler when it is not the final word
    if (POSITIONAL_STOPS.indexOf(tok) !== -1 && pos < tokens.length - 1) continue;

    var matchedStatus = false;
    for (var si = 0; si < STATUS_WORDS.length; si++) {
      for (var sj = 0; sj < STATUS_WORDS[si].stems.length; sj++) {
        if (tokenMatchesStem(tok, STATUS_WORDS[si].stems[sj])) {
          if (!out.status) out.status = STATUS_WORDS[si].value;
          matchedStatus = true;
          break;
        }
      }
      if (matchedStatus) break;
    }
    if (matchedStatus) continue;

    var isFiller = false;
    var groups = [EMPLOYEE_STEMS, COUNT_STEMS, SEARCH_STEMS, STOP_STEMS];
    for (var g = 0; g < groups.length && !isFiller; g++) {
      for (var k = 0; k < groups[g].length; k++) {
        if (tokenMatchesStem(tok, groups[g][k])) {
          isFiller = true;
          break;
        }
      }
    }
    if (isFiller) continue;

    if (tok.length > 1) kept.push(raw(pos));
  }

  // A coverage question goes to the FAQ bot even when it says "الموظف" —
  // unless the user also pinned a status or a department, which only an
  // employee-list question does.
  if (mentionsFaq && !out.status && !out.deptId) {
    return {
      isEmployeeQuery: false,
      wantsCount: false,
      status: '',
      deptId: '',
      deptName: '',
      q: '',
    };
  }

  // A bare status or department («الموقوفين», «المبيعات») is an employee
  // question on this screen; so is a search verb with something left to search.
  out.isEmployeeQuery =
    mentionsEmployees ||
    !!out.status ||
    !!out.deptId ||
    (mentionsSearch && kept.length > 0);
  if (!out.isEmployeeQuery) return out;

  out.q = kept.join(' ').trim();
  return out;
}

/** Which answer the assistant should give — drives the i18n key it renders. */
function assistantOutcome(parsed, result) {
  var p = parsed || {};
  var r = result || {};
  if (!p.isEmployeeQuery) return 'faq';
  if (r.total === 0) return 'employeesNone';
  return p.wantsCount ? 'employeesCount' : 'employeesList';
}

/** Ask-endpoint error → i18n key, so "not working" becomes diagnosable. */
function askErrorKey(err) {
  var e = err || {};
  var res = e.response;
  if (!res) return 'network';
  if (res.status === 429) return 'throttled';
  if (res.status === 403) return 'forbidden';
  if (res.status >= 500) return 'server';
  if (res.status === 400 || res.status === 422) return 'badQuestion';
  return 'generic';
}

module.exports = {
  SKIP_REASONS: SKIP_REASONS,
  MAX_ASSIGN_IDS: MAX_ASSIGN_IDS,
  assignablePolicies: assignablePolicies,
  buildAssignRequest: buildAssignRequest,
  validateAssign: validateAssign,
  summarizeAssign: summarizeAssign,
  assignErrorKey: assignErrorKey,
  toggleId: toggleId,
  toggleAll: toggleAll,
  STATUS_TAG: STATUS_TAG,
  statusTag: statusTag,
  groupNationalId: groupNationalId,
  initials: initials,
  avatarClass: avatarClass,
  buildDeptMap: buildDeptMap,
  deptName: deptName,
  entityName: entityName,
  pageInfo: pageInfo,
  hasPrev: hasPrev,
  hasNext: hasNext,
  buildListParams: buildListParams,
  activeFilterCount: activeFilterCount,
  DELETE_API_ERRORS: DELETE_API_ERRORS,
  deleteErrorKey: deleteErrorKey,
  STATUS_WORDS: STATUS_WORDS,
  normalizeArabic: normalizeArabic,
  stripAl: stripAl,
  tokenMatchesStem: tokenMatchesStem,
  findDepartment: findDepartment,
  POSITIONAL_STOPS: POSITIONAL_STOPS,
  parseEmployeeQuery: parseEmployeeQuery,
  assistantOutcome: assistantOutcome,
  askErrorKey: askErrorKey,
};
